import { base64UrlToBytes } from '../api/base64url'
import { api } from '../api/client'
import type { NewPushSubscription } from '../api/generated/NewPushSubscription'

export type PushStatus = 'unsupported' | NotificationPermission

/** Whether this device can receive pushes, and whether the user allowed them. */
export function pushStatus(): PushStatus {
  // The service worker, which receives pushes, is only registered in production builds
  if (
    process.env.NODE_ENV !== 'production' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  )
    return 'unsupported'
  return Notification.permission
}

/** The API body for a browser subscription; `null` when it carries no encryption keys. */
export function subscriptionBody(json: PushSubscriptionJSON): NewPushSubscription | null {
  const { endpoint, keys } = json
  if (!endpoint || !keys?.p256dh || !keys.auth) return null
  return { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } }
}

/** Subscribes this device, or reuses its subscription, and saves it for the current user. */
export async function subscribe(publicKey: string): Promise<void> {
  const { pushManager } = await navigator.serviceWorker.ready
  const subscription =
    (await pushManager.getSubscription()) ??
    (await pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(publicKey),
    }))
  const body = subscriptionBody(subscription.toJSON())
  if (!body) throw new Error('Push subscription without keys')
  await api.post('/push/subscriptions', body)
}

/** Ends this device's subscription, so the next account on it gets nothing meant for this one. */
export async function unsubscribe(): Promise<void> {
  if (pushStatus() === 'unsupported') return
  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()
  await subscription?.unsubscribe()
}
